import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';

const GET_ACTIVE_TAGS = gql`
  query GetActiveTags {
    activeTags {
      success
      message
      tags {
        id
        name
        description
        usageCount
      }
    }
  }
`;

// 標籤 Context
const TagContext = createContext({
  tags: [],
  loading: false,
  error: null,
  refreshTags: () => {},
  getTagById: (id) => null,
  getTagByName: (name) => null,
  getTagsByIds: (ids) => []
});

// TagProvider 組件
export const TagProvider = ({ children }) => {
  const [tags, setTags] = useState([]);
  const { data, loading, error, refetch } = useQuery(GET_ACTIVE_TAGS, {
    fetchPolicy: 'cache-and-network', // 使用快取但同時從網路獲取最新資料
    errorPolicy: 'all', // 即使有錯誤也返回部分數據
    onCompleted: (data) => {
      console.log('TagContext: Query completed, data:', data);
      if (data?.activeTags) {
        if (data.activeTags.success && data.activeTags.tags) {
          setTags(data.activeTags.tags);
          console.log('TagContext: Loaded tags:', data.activeTags.tags.length);
        } else {
          console.warn('TagContext: Query returned but success=false:', data.activeTags.message);
          // 即使 success=false，也嘗試使用 tags（如果有）
          if (data.activeTags.tags && data.activeTags.tags.length > 0) {
            setTags(data.activeTags.tags);
            console.log('TagContext: Using tags despite success=false:', data.activeTags.tags.length);
          }
        }
      } else {
        console.warn('TagContext: No activeTags in response');
      }
    },
    onError: (error) => {
      console.error('TagContext: Error loading tags:', error);
      console.error('TagContext: Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      });
    }
  });

  // 當 data 變化時也更新 tags（處理非 onCompleted 的情況）
  useEffect(() => {
    if (data?.activeTags) {
      if (data.activeTags.success && data.activeTags.tags) {
        setTags(data.activeTags.tags);
      } else if (data.activeTags.tags && data.activeTags.tags.length > 0) {
        // 即使 success=false，如果有 tags 也使用
        setTags(data.activeTags.tags);
      }
    }
  }, [data]);

  // 手動刷新標籤
  const refreshTags = useCallback(async () => {
    console.log('TagContext: Refreshing tags...');
    try {
      const { data, error: refetchError } = await refetch({
        fetchPolicy: 'network-only' // 強制從網路獲取
      });
      
      if (refetchError) {
        console.error('TagContext: Refetch error:', refetchError);
        return;
      }
      
      if (data?.activeTags) {
        if (data.activeTags.success && data.activeTags.tags) {
          setTags(data.activeTags.tags);
          console.log('TagContext: Tags refreshed:', data.activeTags.tags.length);
        } else if (data.activeTags.tags && data.activeTags.tags.length > 0) {
          // 即使 success=false，如果有 tags 也使用
          setTags(data.activeTags.tags);
          console.log('TagContext: Using tags despite success=false:', data.activeTags.tags.length);
        } else {
          console.warn('TagContext: Refetch returned no tags:', data.activeTags.message);
        }
      }
    } catch (error) {
      console.error('TagContext: Error refreshing tags:', error);
      console.error('TagContext: Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      });
    }
  }, [refetch]);

  // 根據 ID 獲取標籤
  const getTagById = useCallback((id) => {
    return tags.find(tag => tag.id === id) || null;
  }, [tags]);

  // 根據名稱獲取標籤
  const getTagByName = useCallback((name) => {
    return tags.find(tag => tag.name === name) || null;
  }, [tags]);

  // 根據 ID 陣列獲取標籤
  const getTagsByIds = useCallback((ids) => {
    if (!ids || ids.length === 0) return [];
    return tags.filter(tag => ids.includes(tag.id));
  }, [tags]);

  // 記錄狀態變化（用於調試）
  useEffect(() => {
    console.log('TagContext: State update', {
      tagsCount: tags.length,
      loading,
      hasError: !!error,
      errorMessage: error?.message
    });
  }, [tags.length, loading, error]);

  const value = {
    tags,
    loading,
    error,
    refreshTags,
    getTagById,
    getTagByName,
    getTagsByIds
  };

  return (
    <TagContext.Provider value={value}>
      {children}
    </TagContext.Provider>
  );
};

// 自定義 Hook 來使用 TagContext
export const useTags = () => {
  const context = useContext(TagContext);
  if (!context) {
    throw new Error('useTags must be used within a TagProvider');
  }
  return context;
};

export default TagContext;

